import { Capacitor } from '@capacitor/core';

// Tracks the in-flight (or completed) permission check so showLocalNotification
// can await it instead of racing a module-level boolean that initLocalNotifications
// sets asynchronously — without this, a notification arriving before init
// resolves would silently be dropped.
let readyPromise = null;

/**
 * Requests permission to show local notifications. Native-only, no-ops on
 * web. This is the "notification banner on the phone" substitute for real
 * push — see the comment in App.js for why this exists instead.
 */
export function initLocalNotifications() {
  if (!Capacitor.isNativePlatform()) {
    readyPromise = Promise.resolve(false);
    return readyPromise;
  }

  readyPromise = (async () => {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const permission = await LocalNotifications.checkPermissions();
    if (permission.display === 'granted') return true;
    const requested = await LocalNotifications.requestPermissions();
    return requested.display === 'granted';
  })();

  return readyPromise;
}

/** Shows an immediate local notification. `data` is retrievable on tap via `extra`. */
export async function showLocalNotification(title, body, data = {}) {
  if (!Capacitor.isNativePlatform()) return;

  const granted = await (readyPromise || initLocalNotifications());
  if (!granted) return;

  const { LocalNotifications } = await import('@capacitor/local-notifications');
  await LocalNotifications.schedule({
    notifications: [
      {
        // 32-bit int id — timestamp mod a safe range keeps concurrent
        // notifications from colliding while staying in range.
        id: Date.now() % 2147483647,
        title,
        body,
        extra: data,
      },
    ],
  });
}

/** Registers the tap handler once. Returns an unsubscribe function. */
export async function onLocalNotificationTap(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const { LocalNotifications } = await import('@capacitor/local-notifications');
  const handle = await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    callback(action.notification?.extra || {});
  });
  return () => handle.remove();
}
